import { useMutation } from "@apollo/client";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MergeIcon from "@mui/icons-material/Merge";
import { Box, Button, Grid, Typography, useTheme } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { gql } from "generated-graphql";
import { useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useWasteFacilityState } from "../../WasteFacilityContext";
import { MergedDestinationsDataGrid } from "./MergedDestinationsDataGrid";
import { MergedWasteStreamInfo } from "./MergedWasteStreamInfo";
import { MergedWasteStreamInternalNotesInput } from "./MergedWasteStreamInternalNotesInput";
import { MergedWasteStreamNameInput } from "./MergedWasteStreamNameInput";
import { MergedWasteStreamsDataGrid } from "./MergedWasteStreamsDataGrid";

const MERGE_WASTE_STREAMS = gql(`
  mutation MergeWasteStreams($input: MergeWasteStreamsInput!) {
    mergeWasteStreams(input: $input)
  }
`);

interface WasteStreamMergeFormData {
  comment?: string;
  internalNote?: string;
  wasteStreamName: string;
  wasteStreamIds: string[];
}

interface WasteStreamMergeDialogProps {
  onClose: () => void;
  selectedRows: any[]; // Replace with proper type when available
}

export const WasteStreamMergeDialog = ({
  onClose,
  selectedRows,
}: WasteStreamMergeDialogProps) => {
  const theme = useTheme();
  const alerts = useAlerts();
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const {
    wasteFacilityState: { deferredFilters },
  } = useWasteFacilityState();

  const [mergeWasteStreams, { loading: merging }] = useMutation(
    MERGE_WASTE_STREAMS,
    {
      update: (cache, { data }) => {
        // Evict the merged waste stream from cache before refetch
        if (data?.mergeWasteStreams) {
          cache.evict({
            id: cache.identify({
              __typename: "WasteStream",
              id: data.mergeWasteStreams,
            }),
          });
          cache.gc();
        }
      },
      refetchQueries: ["WasteStreams"], // Refresh the waste streams query
      awaitRefetchQueries: true,
    }
  );

  const wasteStreamNames = useMemo(
    () => selectedRows.map((row) => row.name),
    [selectedRows]
  );

  const methods = useForm<WasteStreamMergeFormData>({
    defaultValues: {
      comment:
        selectedRows
          .map((row) => row.comment?.trim())
          .filter(Boolean)
          .join("; ") || "",
      internalNote: "",
      wasteStreamName: selectedRows[0]?.name || "",
      wasteStreamIds: selectedRows.map((row) => row.id),
    },
  });

  const { control, handleSubmit } = methods;

  const handleNext = () => {
    if (currentStep === 1) {
      setCurrentStep(2);
    } else {
      // Step 2: Submit the form
      handleSubmit(async (data) => {
        try {
          const result = await mergeWasteStreams({
            variables: {
              input: {
                wasteStreamIds: data.wasteStreamIds,
                name: data.wasteStreamName.trim(),
                comment: data.comment?.trim(),
                internalNote: data.internalNote?.trim() || undefined,
              },
            },
          });

          if (result.errors) {
            alerts.error(
              "Failed to merge waste streams",
              result.errors[0].message
            );
          } else if (result.data?.mergeWasteStreams) {
            alerts.success("Waste streams merged successfully");
          }

          onClose(); // Always close and clear selections
        } catch (error) {
          // Handle network or other unexpected errors
          if (error instanceof Error) {
            alerts.error("Failed to merge waste streams", error.message);
          } else {
            alerts.error(
              "Failed to merge waste streams",
              "An unexpected error occurred"
            );
          }
          onClose(); // Close even on error
        }
      })();
    }
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setCurrentStep(1);
    }
  };

  const getTitle = () => {
    if (currentStep === 1) {
      return `Merge ${selectedRows.length} waste stream${
        selectedRows.length === 1 ? "" : "s"
      }`;
    }
    return "Review merged waste streams";
  };

  const dateRangeText = useMemo(() => {
    const startDate =
      deferredFilters.startDate &&
      getUtcDisplayDate(deferredFilters.startDate, "short");
    const endDate =
      deferredFilters.endDate &&
      getUtcDisplayDate(deferredFilters.endDate, "short");
    if (!startDate || !endDate) return "";
    return `${startDate}-${endDate}`;
  }, [deferredFilters.startDate, deferredFilters.endDate]);

  return (
    <FormProvider {...methods}>
      <Dialog
        open={true}
        onClose={onClose}
        title={getTitle()}
        maxWidth="lg"
        fullWidth
        noPrimaryAction={true}
        secondaryActionElements={[
          <Button key="cancel" onClick={onClose}>
            Cancel
          </Button>,
        ]}
        additionalPrimaryActionElements={[
          <Typography
            key="step-indicator"
            variant="body2"
            sx={{
              color: "text.secondary",
              mr: 2,
            }}
          >
            Step {currentStep}/2
          </Typography>,
          <Button
            key="next"
            variant="contained"
            onClick={handleNext}
            disabled={merging}
            startIcon={currentStep === 2 ? <MergeIcon /> : undefined}
            endIcon={currentStep === 1 ? <ChevronRightIcon /> : undefined}
            sx={{
              color: theme.palette.primary.contrastText,
              backgroundColor: theme.palette.warning.light,
            }}
          >
            {currentStep === 1
              ? "Review Merge"
              : merging
              ? "Merging..."
              : "Merge"}
          </Button>,
        ]}
      >
        <Box>
          {/* Back button shown only on step 2 */}
          {currentStep === 2 && (
            <Button
              startIcon={<ChevronLeftIcon />}
              onClick={handleBack}
              sx={{
                mb: 1,
                mt: theme.spacing(-1.5), // Negative margin to reduce spacing from header
                justifyContent: "flex-start",
              }}
            >
              Back
            </Button>
          )}

          {/* Helper text shown on both steps */}
          <Typography sx={{ mb: 3, color: "text.secondary" }}>
            This merge affects these waste streams for <strong>all time</strong>
            , not just the selected time range ({dateRangeText})
          </Typography>

          {/* Step-specific content */}
          {currentStep === 1 && (
            <MergedWasteStreamsDataGrid
              wasteStreams={selectedRows}
              dateRangeText={dateRangeText}
            />
          )}

          {currentStep === 2 && (
            <Box>
              <Grid container spacing={3}>
                {/* First row - editable field */}
                <Grid size={{ xs: 12 }}>
                  <MergedWasteStreamNameInput
                    control={control}
                    wasteStreamNames={wasteStreamNames}
                  />
                </Grid>
              </Grid>

              {/* Read-only waste stream info */}
              <Box sx={{ mt: 3 }}>
                <MergedWasteStreamInfo
                  wasteStreams={selectedRows}
                  dateRangeText={dateRangeText}
                />
              </Box>
            </Box>
          )}

          {/* Internal Note field shown on both steps */}
          <Box sx={{ mt: 3 }}>
            <MergedWasteStreamInternalNotesInput control={control} />
          </Box>

          {/* Merged Destinations Data Grid - Only on Step 2 */}
          {currentStep === 2 && (
            <MergedDestinationsDataGrid
              wasteStreamIds={selectedRows.map((row) => row.id)}
            />
          )}
        </Box>
      </Dialog>
    </FormProvider>
  );
};
